import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import * as platformService from "../../../services/platformService";
import { buildWorkflowPayload } from "../../HR/settings/WorkflowsTab";
import { FormFieldsLite } from "../../HR/settings/workflowParts";
import { AppDropdown, EmployeeSelect, EmptyState, Field, LoadState, PrimaryButton, SecondaryButton, SlidePanel, cardStyle, iconBtnStyle, inputStyle, statusBadge, statusLabel, tdStyle, thStyle, useLoad } from "../../../components/ui";

// Who approves what in Finance / the Dealership. The same builder as HR's approval
// workflows, on the shared approval engine: each request type has a default
// workflow; extra ones take over when their conditions match (e.g. a discount over
// 10% needs the business unit manager too). Approvers are employees.
const linkBtn = { ...iconBtnStyle, fontSize: 13, fontWeight: 600 };

const APPROVER_LABELS = {
  MANAGER: "Reporting manager",
  MANAGER_N: "Manager’s manager (level 2–5)",
  DEPT_HEAD: "Department head",
  SITE_INCHARGE: "Site in-charge",
  EMPLOYEE: "A named employee",
  PERMISSION: "Anyone with a permission",
  BU_MANAGER: "Business unit manager",
  LOCATION_MANAGER: "Location manager",
};
const ESCALATION_LABELS = {
  NONE: "Do nothing",
  ESCALATE_NEXT: "Escalate to the next level",
  ESCALATE_ADMIN: "Escalate to the module administrator",
  AUTO_APPROVE: "Approve automatically",
  AUTO_REJECT: "Reject automatically",
};
// Conditions on ids (business unit, location, department) would need raw ids the portal never sees,
// so the builder offers the numeric ones - the same choice HR's builder makes.
const CONDITION_FIELDS = [
  { label: "Amount", value: "AMOUNT" },
  { label: "Discount %", value: "DISCOUNT_PCT" },
];
const OPS = ["GT", "GTE", "LT", "LTE", "EQ", "NE"];
const OP_LABEL = { EQ: "is", NE: "is not", GT: "more than", GTE: "at least", LT: "less than", LTE: "at most" };

const newLevel = () => ({ approver_type: "PERMISSION", approver_ref: "", approver_employee_id: null, mode: "ANY", sla_hours: "", escalation: "NONE" });

function WorkflowDrawer({ module, meta, show, workflow, onHide, onDone }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!show) return;
    setForm(
      workflow
        ? {
            id: workflow.id,
            name: workflow.name,
            request_type: workflow.request_type,
            is_default: !!workflow.is_default,
            priority: workflow.priority,
            levels: workflow.levels.map((l) => ({ ...l, sla_hours: l.sla_hours ?? "", approver_ref: l.approver_ref ?? "" })),
            conditions: workflow.conditions.map((c) => ({ ...c })),
          }
        : { name: "", request_type: meta.request_types[0]?.value, is_default: false, priority: 100, levels: [newLevel()], conditions: [] }
    );
  }, [show, workflow, meta]);
  if (!form) return null;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setLevel = (i, patch) => set({ levels: form.levels.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const setCond = (i, patch) => set({ conditions: form.conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)) });

  const save = async () => {
    setSaving(true);
    try {
      const res = await platformService.saveWorkflow(module, workflow?.id, buildWorkflowPayload(form));
      if (res?.success) {
        toast.success(res.message || "Saved");
        onDone();
        onHide();
      } else toast.error(platformService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };

  const approverTypes = meta.approver_types.map((v) => ({ label: APPROVER_LABELS[v] || statusLabel(v), value: v }));
  const escalations = meta.escalations.map((v) => ({ label: ESCALATION_LABELS[v] || statusLabel(v), value: v }));

  return (
    <SlidePanel
      show={show}
      onHide={() => !saving && onHide()}
      title={workflow ? "Edit workflow" : "New workflow"}
      width={640}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving || !form.name.trim()}>
            Save
          </PrimaryButton>
        </>
      }
    >
      <Field label="Name" required htmlFor="pwf-name">
        <input id="pwf-name" className="form-control" style={inputStyle} maxLength={100} value={form.name} onChange={(e) => set({ name: e.target.value })} />
      </Field>
      <Field label="Request type" required>
        <AppDropdown value={form.request_type} options={meta.request_types} onChange={(e) => set({ request_type: e.value })} disabled={!!workflow} />
      </Field>
      <div className="form-check mb-3">
        <input id="pwf-default" type="checkbox" className="form-check-input" checked={form.is_default} onChange={(e) => set({ is_default: e.target.checked })} />
        <label htmlFor="pwf-default" className="form-check-label" style={{ fontSize: 14 }}>
          The default for this request type (used when no other workflow’s conditions match)
        </label>
      </div>

      {!form.is_default ? (
        <>
          <h6>When</h6>
          {form.conditions.map((c, i) => (
            <div key={i} className="d-flex flex-wrap gap-2 align-items-center mb-2">
              <div style={{ width: 160 }}>
                <AppDropdown value={c.field} options={CONDITION_FIELDS} onChange={(e) => setCond(i, { field: e.value })} />
              </div>
              <div style={{ width: 140 }}>
                <AppDropdown value={c.operator} options={OPS.map((o) => ({ label: OP_LABEL[o], value: o }))} onChange={(e) => setCond(i, { operator: e.value })} />
              </div>
              <input type="number" className="form-control" style={{ ...inputStyle, width: 130 }} value={c.value} onChange={(e) => setCond(i, { value: e.target.value })} placeholder="e.g. 10" aria-label="Condition value" />
              <button type="button" style={linkBtn} onClick={() => set({ conditions: form.conditions.filter((_, j) => j !== i) })}>
                Remove
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-outline-secondary btn-sm mb-3" onClick={() => set({ conditions: [...form.conditions, { field: "AMOUNT", operator: "GT", value: "" }] })}>
            Add condition
          </button>
          <Field label="Priority" hint="Lower numbers are checked first.">
            <input type="number" className="form-control" style={{ ...inputStyle, maxWidth: 140 }} min={1} value={form.priority} onChange={(e) => set({ priority: e.target.value })} aria-label="Priority" />
          </Field>
        </>
      ) : null}

      <h6 className="mt-2">Approval levels</h6>
      {form.levels.map((l, i) => (
        <div key={i} style={{ ...cardStyle, padding: 14, marginBottom: 12, border: "1px solid #E5E7EB", boxShadow: "none" }}>
          <div className="d-flex justify-content-between mb-2">
            <strong>Level {i + 1}</strong>
            {form.levels.length > 1 ? (
              <button type="button" style={linkBtn} onClick={() => set({ levels: form.levels.filter((_, j) => j !== i) })}>
                Remove
              </button>
            ) : null}
          </div>
          <FormFieldsLite>
            <Field label="Approver">
              <AppDropdown value={l.approver_type} options={approverTypes} onChange={(e) => setLevel(i, { approver_type: e.value })} />
            </Field>
            {l.approver_type === "MANAGER_N" ? (
              <Field label="Levels up (2–5)">
                <input type="number" className="form-control" style={inputStyle} min={2} max={5} value={l.approver_ref} onChange={(e) => setLevel(i, { approver_ref: e.target.value })} aria-label="Levels up" />
              </Field>
            ) : null}
            {l.approver_type === "PERMISSION" ? (
              <Field label="Permission">
                <AppDropdown value={l.approver_ref || null} options={meta.permissions} onChange={(e) => setLevel(i, { approver_ref: e.value })} placeholder="Choose" filter />
              </Field>
            ) : null}
            {l.approver_type === "EMPLOYEE" ? (
              <Field label="Employee">
                <EmployeeSelect value={l.approver_employee_id} onChange={(id) => setLevel(i, { approver_employee_id: id })} />
              </Field>
            ) : null}
            <Field label="When several approvers">
              <AppDropdown value={l.mode} options={[{ label: "Any one can approve", value: "ANY" }, { label: "All must approve", value: "ALL" }]} onChange={(e) => setLevel(i, { mode: e.value })} />
            </Field>
            <Field label="Reply within (hours)">
              <input type="number" className="form-control" style={inputStyle} min={1} value={l.sla_hours} onChange={(e) => setLevel(i, { sla_hours: e.target.value })} aria-label="Reply within hours" />
            </Field>
            <Field label="If not answered in time">
              <AppDropdown value={l.escalation} options={escalations} onChange={(e) => setLevel(i, { escalation: e.value })} />
            </Field>
          </FormFieldsLite>
        </div>
      ))}
      {form.levels.length < 5 ? (
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => set({ levels: [...form.levels, newLevel()] })}>
          Add level
        </button>
      ) : null}
    </SlidePanel>
  );
}

export default function WorkflowSetup({ module }) {
  const { data, loading, error, reload } = useLoad(() => platformService.listWorkflows(module), [module]);
  const meta = useLoad(() => platformService.getWorkflowMeta(module), [module]);
  const [edit, setEdit] = useState(undefined);
  const rows = data || [];
  const typeLabel = (t) => (meta.data?.request_types || []).find((x) => x.value === t)?.label || statusLabel(t);
  const permLabel = (p) => (meta.data?.permissions || []).find((x) => x.value === p)?.label || statusLabel(p);
  const describeLevel = (l) => (l.approver_type === "PERMISSION" ? permLabel(l.approver_ref) : l.approver_type === "MANAGER_N" ? `Manager +${l.approver_ref}` : APPROVER_LABELS[l.approver_type] || l.approver_type);

  const deactivate = async (w) => {
    const res = await platformService.deactivateWorkflow(module, w.id).catch((e) => ({ success: false, message: e.message }));
    if (res?.success) {
      toast.success(res.message || "Done");
      reload();
    } else toast.error(platformService.responseMessage(res));
  };

  return (
    <>
      <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 12 }}>
        Every request type starts with a default workflow. Requests and decisions appear in the Approvals inbox, next to HR requests.
      </div>
      <div className="mb-3">
        <PrimaryButton onClick={() => setEdit(null)} disabled={!meta.data}>
          New workflow
        </PrimaryButton>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="No workflows" />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  {["Request", "Workflow", "Approval path", "Status", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((w) => (
                  <tr key={w.id}>
                    <td style={tdStyle}>{typeLabel(w.request_type)}</td>
                    <td style={tdStyle}>
                      {w.name}
                      {w.is_default ? <span style={{ ...statusBadge("PROBATION"), marginLeft: 8 }}>Default</span> : null}
                      {!w.is_default && w.conditions.length ? (
                        <div style={{ fontSize: 12, color: "#6B7280" }}>{w.conditions.map((c) => `${(CONDITION_FIELDS.find((f) => f.value === c.field) || { label: c.field }).label.toLowerCase()} ${OP_LABEL[c.operator] || c.operator} ${c.value}`).join(" and ")}</div>
                      ) : null}
                    </td>
                    <td style={tdStyle}>{w.levels.map(describeLevel).join(" → ")}</td>
                    <td style={tdStyle}>
                      <span style={statusBadge(w.status)}>{statusLabel(w.status)}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" style={linkBtn} onClick={() => setEdit(w)} disabled={!meta.data}>
                        Edit
                      </button>
                      {w.status === "ACTIVE" && !w.is_default ? (
                        <>
                          {" "}
                          <button type="button" style={linkBtn} onClick={() => deactivate(w)}>
                            Deactivate
                          </button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      {meta.data ? <WorkflowDrawer module={module} meta={meta.data} show={edit !== undefined} workflow={edit || null} onHide={() => setEdit(undefined)} onDone={reload} /> : null}
    </>
  );
}
